import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('verifying'); // verifying, activating, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [pollCount, setPollCount] = useState(0);
  const MAX_POLLS = 10;

  // Verify the checkout session server-side
  const verifySession = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('No session ID provided. Please try the purchase again.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE}/api/billing/verify-session`,
        { session_id: sessionId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data.success && response.data.is_pro) {
        setStatus('success');
      } else if (response.data.success && !response.data.is_pro) {
        // Payment succeeded but Pro status not yet active (webhook pending)
        setStatus('activating');
      } else {
        setStatus('error');
        setErrorMessage(response.data.message || 'Could not verify payment.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      if (error.response?.status === 404) {
        setStatus('error');
        setErrorMessage('Session not found. It may have expired.');
      } else {
        setStatus('error');
        setErrorMessage('Could not verify payment. Please check your billing page.');
      }
    }
  }, [sessionId]);

  // Poll for Pro status if webhook is delayed
  const pollProStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatus('error');
        setErrorMessage('Please log in to verify your subscription.');
        return;
      }

      const response = await axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.plan === 'pro') {
        setStatus('success');
        // Update local storage with new user data
        localStorage.setItem('user', JSON.stringify(response.data));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Poll error:', error);
      return false;
    }
  }, []);

  // Initial verification
  useEffect(() => {
    verifySession();
  }, [verifySession]);

  // Poll for Pro status when in activating state
  useEffect(() => {
    if (status !== 'activating') return;
    if (pollCount >= MAX_POLLS) {
      setStatus('success'); // Assume success after max polls, webhook will catch up
      return;
    }

    const timer = setTimeout(async () => {
      const isPro = await pollProStatus();
      if (!isPro) {
        setPollCount(prev => prev + 1);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearTimeout(timer);
  }, [status, pollCount, pollProStatus]);

  // Auto-redirect countdown when successful
  useEffect(() => {
    if (status !== 'success') return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/app');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, navigate]);

  // Refresh user data in context/storage
  const refreshUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        localStorage.setItem('user', JSON.stringify(response.data));
        // Dispatch event for other components to refresh
        window.dispatchEvent(new Event('userUpdated'));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, []);

  useEffect(() => {
    if (status === 'success') {
      refreshUserData();
    }
  }, [status, refreshUserData]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F1A' }}>
      <div className="max-w-md w-full mx-4">
        <div className="text-center">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className="h-12 w-auto"
              style={{ filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))' }}
            />
            <span className="text-2xl font-semibold tracking-tight" style={{ color: '#F8FAFC' }}>
              ScopeTheMarket
            </span>
          </Link>

          {/* Status Card */}
          <div 
            className="p-8 rounded-2xl"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            {/* Verifying State */}
            {status === 'verifying' && (
              <>
                <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: '#3B82F6' }} />
                <h1 className="text-2xl font-bold mb-2" style={{ color: '#F8FAFC' }}>
                  Verifying Payment...
                </h1>
                <p style={{ color: '#94A3B8' }}>
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {/* Activating State (webhook pending) */}
            {status === 'activating' && (
              <>
                <div className="relative">
                  <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: '#22C55E' }} />
                </div>
                <h1 className="text-2xl font-bold mb-2" style={{ color: '#F8FAFC' }}>
                  Activating Pro...
                </h1>
                <p className="mb-4" style={{ color: '#94A3B8' }}>
                  Your payment was successful! Activating your Pro membership...
                </p>
                <div className="flex items-center justify-center gap-2 text-sm" style={{ color: '#64748B' }}>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>This usually takes a few seconds</span>
                </div>
              </>
            )}

            {/* Success State */}
            {status === 'success' && (
              <>
                <div 
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}
                >
                  <CheckCircle className="h-10 w-10" style={{ color: '#22C55E' }} />
                </div>
                <h1 className="text-2xl font-bold mb-2" style={{ color: '#F8FAFC' }}>
                  Welcome to Pro!
                </h1>
                <p className="mb-2" style={{ color: '#94A3B8' }}>
                  Thank you for your payment — you are now a Pro member and can access all features.
                </p>
                <p className="mb-6 text-sm" style={{ color: '#64748B' }}>
                  Redirecting to dashboard in {countdown} seconds...
                </p>
                <div className="space-y-3">
                  <Link
                    to="/app"
                    className="block w-full py-3 px-4 rounded-lg font-medium text-white text-center transition-colors hover:opacity-90"
                    style={{ backgroundColor: '#3B82F6' }}
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard Now
                  </Link>
                  <Link
                    to="/billing"
                    className="block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors hover:bg-white/10"
                    style={{ color: '#94A3B8', backgroundColor: 'rgba(255,255,255,0.05)' }}
                  >
                    View Subscription Details
                  </Link>
                </div>
              </>
            )}

            {/* Error State */}
            {status === 'error' && (
              <>
                <div 
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}
                >
                  <XCircle className="h-10 w-10" style={{ color: '#EF4444' }} />
                </div>
                <h1 className="text-2xl font-bold mb-2" style={{ color: '#F8FAFC' }}>
                  Verification Issue
                </h1>
                <p className="mb-6" style={{ color: '#94A3B8' }}>
                  {errorMessage}
                </p>
                <div className="space-y-3">
                  <Link
                    to="/billing"
                    className="block w-full py-3 px-4 rounded-lg font-medium text-white text-center transition-colors hover:opacity-90"
                    style={{ backgroundColor: '#3B82F6' }}
                  >
                    Check Billing Status
                  </Link>
                  <button
                    onClick={() => {
                      setStatus('verifying');
                      setErrorMessage('');
                      verifySession();
                    }}
                    className="block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors hover:bg-white/10"
                    style={{ color: '#94A3B8', backgroundColor: 'rgba(255,255,255,0.05)' }}
                  >
                    Try Again
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Session ID for debugging */}
          {sessionId && (
            <p className="mt-4 text-xs" style={{ color: '#64748B' }}>
              Session: {sessionId.substring(0, 20)}...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSuccess;
